export const LANGUAGES = [
    { code: 'en', label: 'English (/en)', isDefault: true },
    { code: 'de', label: 'German (/de)' },
    { code: 'fr', label: 'French (/fr)' },
    { code: 'br', label: 'Portuguese (/br)' },
    { code: 'es', label: 'Spanish (/es)' },
];

export const CURRENCIES = [
    { code: 'USD', label: 'USD - United States Dollar', isDefault: true },
    { code: 'GBP', label: 'GBP - British Pound' },
    { code: 'BRL', label: 'BRL - Brazilian Real' },
    { code: 'NZD', label: 'NZD - New Zealand Dollar' },
    { code: 'AUD', label: 'AUD - Australian Dollar' },
    { code: 'MXN', label: 'MXN - Mexican Peso' },
    { code: 'EUR', label: 'EUR - Euro' },
    { code: 'IRS', label: 'IRS' },
    { code: 'SGD', label: 'SGD - Singapore Dollar' },
];

const SUPPORTED_LANGUAGE_CODES = new Set(LANGUAGES.map(language => language.code));

const getPathLanguage = (pathname: string): string | null => {
    const firstSegment = pathname.split('/').filter(Boolean)[0];
    return firstSegment && SUPPORTED_LANGUAGE_CODES.has(firstSegment) ? firstSegment : null;
};

const getUrlLanguage = (urlOrPath: string): string | null => {
    try {
        return getPathLanguage(new URL(urlOrPath).pathname);
    } catch {
        return getPathLanguage(urlOrPath.split('?')[0].split('#')[0]);
    }
};

const withLanguagePrefix = (pathname: string, language: string): string => {
    if (getPathLanguage(pathname)) {
        return pathname;
    }

    const hasTrailingSlash = pathname.endsWith('/');
    const normalizedPath = pathname === '/' ? '' : pathname;
    const result = `/${language}${normalizedPath}`;
    return hasTrailingSlash && !result.endsWith('/') ? `${result}/` : result;
};

interface TrackingParams {
    baseUrl: string;
    affiliateId: string;
    language: string;
    currency: string;
    productId?: string;
    landingPageId?: string;
    creatives?: {
        landing_pages?: { id: string; name: string; url: string }[];
    };
    landing_pages?: { id: string; name: string; url: string }[];
    clickId?: string;
    subId?: string;
    sourceId?: string;
}

export const generateTrackingUrl = (params: TrackingParams): string => {
    const {
        baseUrl,
        affiliateId,
        language,
        currency,
        productId,
        landingPageId,
        creatives,
        landing_pages,
        clickId,
        subId,
        sourceId
    } = params;

    try {
        let finalBaseUrl = baseUrl;
        let entryLinkIdParam = '';

        if (landingPageId && landingPageId !== 'default') {
            // Check top-level landing_pages first
            let lp = landing_pages?.find(l => l.id === landingPageId);

            // Fallback to creatives.landing_pages for backwards compatibility
            if (!lp) {
                lp = creatives?.landing_pages?.find(l => l.id === landingPageId);
            }

            if (lp) {
                finalBaseUrl = lp.url;
                entryLinkIdParam = lp.id;
            }
        }

        const url = new URL(finalBaseUrl);

        url.pathname = withLanguagePrefix(url.pathname, language);

        // Set query params
        url.searchParams.set('aff_id', affiliateId);
        if (productId) url.searchParams.set('pid', productId);
        url.searchParams.set('currency', currency);
        if (clickId) url.searchParams.set('click_id', clickId);
        if (subId) url.searchParams.set('sub_id', subId);
        if (sourceId) url.searchParams.set('source_id', sourceId);
        if (entryLinkIdParam) url.searchParams.set('el', entryLinkIdParam);

        return url.toString();
    } catch (e) {
        // Fallback for invalid URLs or relative paths
        let finalBaseUrl = baseUrl;
        let entryLinkIdParam = '';
        if (landingPageId && landingPageId !== 'default') {
            // Check top-level landing_pages first
            let lp = landing_pages?.find(l => l.id === landingPageId);

            // Fallback to creatives.landing_pages
            if (!lp) {
                lp = creatives?.landing_pages?.find(l => l.id === landingPageId);
            }

            if (lp) {
                finalBaseUrl = lp.url;
                entryLinkIdParam = lp.id;
            }
        }

        let fallback = finalBaseUrl;

        if (!getUrlLanguage(finalBaseUrl)) {
            const hadTrailingSlash = fallback.endsWith('/');
            if (hadTrailingSlash) fallback = fallback.slice(0, -1);
            fallback = `${fallback}/${language}`;
            if (hadTrailingSlash) fallback += '/';
        }

        const separator = fallback.includes('?') ? '&' : '?';
        fallback = `${fallback}${separator}aff_id=${affiliateId}`;
        if (productId) fallback += `&pid=${encodeURIComponent(productId)}`;
        fallback += `&currency=${currency}`;

        if (clickId) fallback += `&click_id=${encodeURIComponent(clickId)}`;
        if (subId) fallback += `&sub_id=${encodeURIComponent(subId)}`;
        if (sourceId) fallback += `&source_id=${encodeURIComponent(sourceId)}`;
        if (entryLinkIdParam) fallback += `&el=${encodeURIComponent(entryLinkIdParam)}`;
        return fallback;
    }
};
